import { useState } from "react";
import { Alert, Button, Descriptions, Modal, Typography } from "antd";
import { useSwapJoyTgtMutation } from "../../../api/admin/joyTgtApi";
import { Redemption } from "../../../api/redemption/Redemption";

export function EffectiveSku({ row }: { row: Redemption }) {
  return (
    <span>
      <Typography.Text delete={Boolean(row.AlternateSku)}>
        {row.InternalSku}
      </Typography.Text>
      {row.AlternateSku && (
        <>
          <br />
          <Typography.Text>{row.AlternateSku}</Typography.Text>
        </>
      )}
    </span>
  );
}

export function formatJoyTgtDate(date?: string) {
  return date
    ? new Date(date).toLocaleString("en-GB", {
      timeZone: "Asia/Singapore",
      hour12: false,
    }) + " UTC+8"
    : "-";
}

export default function JoyTgtDetails({ row, onSwapped }: {
  row?: Redemption;
  onSwapped?: (updated: Redemption) => void;
}) {
  const [swap, { isLoading }] = useSwapJoyTgtMutation();
  const [confirming, setConfirming] = useState(false);
  const [failure, setFailure] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const audit = row?.JoyTgtReplacement;
  const formatted = formatJoyTgtDate(audit?.ProcessedAt);

  const eligible = row
    && /^(joy|tgt)-/.test(row.InternalSku)
    && !row.IsSent
    && !row.IsCanceled
    && !row.IsManualQr
    && !row.AssignedSimId
    && !row.AlternateSku
    && (!audit || (
      audit.Status === "unknown"
      && audit.PurchaseState === "ready"
      && !audit.OrderCallback
    ));

  async function submit() {
    if (!row || isLoading || submitted) {
      return;
    }
    setFailure("");
    try {
      const updated = await swap({
        orderId: row.OrderId,
        simIdx: row.SimIdx,
        confirmed: true,
      }).unwrap();
      setSubmitted(true);
      setConfirming(false);
      onSwapped?.(updated);
    } catch (error) {
      const response = error as { data?: { message?: string } };
      setFailure(response.data?.message || (
        "Unable to confirm swap outcome. Refresh and check before retrying."
      ));
      setConfirming(false);
      setSubmitted(true);
    }
  }

  return (
    <>
      <Typography.Text type="secondary">Joy-TGT</Typography.Text>
      <Descriptions column={1} bordered size="small">
        <Descriptions.Item label="Status">
          {audit?.Status?.replaceAll("_", " ") || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Processed datetime">
          {formatted}
        </Descriptions.Item>
        <Descriptions.Item label="Initiated by">
          {audit?.InitiatedBy || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Original SIMPLY SKU">
          {row?.InternalSku || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Equivalent replacement SKU">
          {row?.AlternateSku || audit?.ReplacementSku || "-"}
        </Descriptions.Item>
        {audit && (
          <Descriptions.Item label="Provider results">
            <Typography.Paragraph>
              <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {JSON.stringify({
                  originalFailure: audit.OriginalFailure,
                  providerResponse: audit.ProviderResponse,
                  error: audit.Error,
                }, null, 2)}
              </pre>
            </Typography.Paragraph>
          </Descriptions.Item>
        )}
      </Descriptions>
      {eligible && (
        <Button
          onClick={() => setConfirming(true)}
          disabled={submitted}
          loading={isLoading}
        >
          Swap JOY ↔ TGT
        </Button>
      )}
      {submitted && !failure && (
        <Alert type="success" message="Swap requested" />
      )}
      {failure && <Alert type="error" message={failure} />}
      <Modal
        title="Confirm manual Joy-TGT swap"
        open={confirming}
        okText="Confirm and swap"
        confirmLoading={isLoading}
        cancelButtonProps={{ disabled: isLoading }}
        closable={!isLoading}
        maskClosable={!isLoading}
        keyboard={!isLoading}
        onOk={submit}
        onCancel={() => setConfirming(false)}
      >
        I have checked the original provider and confirm that no purchase
        is accepted or pending. Start the replacement using the configured
        equivalent SKU.
      </Modal>
    </>
  );
}
